import React, { useState, useEffect } from 'react';
import ProgressHeader from './components/ProgressHeader';
import ModuleCard from './components/ModuleCard';
import AdminPanel from './components/AdminPanel';
import { getApiUrl, API_CONFIG } from './config';

// WASM will be loaded via main.tsx
declare global {
  interface Window {
    wasm?: any;
  }
}

function App() {
  const [modules, setModules] = useState<any[]>([]);
  const [progress, setProgress] = useState({ 
    total_lessons: 0, 
    completed_lessons: 0, 
    progress_percentage: 0 
  });
  const [wasmReady, setWasmReady] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState<any>(null);
  const [view, setView] = useState<'home' | 'lesson'>('home');
  const [manager, setManager] = useState<any>(null);
  const [showAdmin, setShowAdmin] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadContent() {
      if (window.wasm) {
        const wasm = window.wasm;
        setWasmReady(true);
        
        const newManager = new wasm.ContentManager();
        
        try {
          // Try to fetch from API first
          const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.MODULES));
          if (response.ok) {
            const data = await response.json();
            newManager.importFromJson(JSON.stringify(data));
            console.log('✓ Content loaded from API');
          } else {
            throw new Error('API failed');
          }
        } catch (e) {
          console.warn('API not available, falling back to local storage or defaults');
          // Fallback to localStorage
          const savedContent = localStorage.getItem('app_curso_content_json');
          if (savedContent) {
            try {
              newManager.importFromJson(savedContent);
            } catch (err) {
              console.error('Failed to import saved content', err);
              newManager.loadDefaultContent();
            }
          } else {
            newManager.loadDefaultContent();
          }
        }
        
        setManager(newManager);
        setModules(newManager.modules);
        setProgress(newManager.getProgress());
        
        // Load saved progress if available
        const savedProgress = localStorage.getItem('app_curso_completed_lessons');
        if (savedProgress) {
          try {
            const lessonIds = JSON.parse(savedProgress);
            lessonIds.forEach((id: number) => newManager.markLessonCompleted(id));
            setModules(newManager.modules);
            setProgress(newManager.getProgress());
          } catch (e) {
            console.error('Failed to load progress', e);
          }
        }
        
        console.log('✓ WASM available and state initialized');
        setLoading(false);
      }
    }

    loadContent();
  }, [wasmReady]); // Re-run when wasmReady becomes true if it wasn't already

  // Check for WASM availability more robustly
  useEffect(() => {
    const interval = setInterval(() => {
      if (window.wasm && !wasmReady) {
        setWasmReady(true);
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [wasmReady]);

  const refreshData = () => {
    if (manager) {
      setModules(manager.modules);
      setProgress(manager.getProgress());
      
      // Update selected lesson if we are in lesson view
      if (selectedLesson) {
        const updatedLesson = manager.modules
          .flatMap((m: any) => m.lessons)
          .find((l: any) => l.id === selectedLesson.id);
        if (updatedLesson) setSelectedLesson(updatedLesson);
      }
    }
  };

  const handleLessonSelect = (lesson: any) => {
    setSelectedLesson(lesson);
    setView('lesson');
    window.scrollTo(0, 0);
  };

  const handleMarkCompleted = (lessonId: number) => {
    if (manager) {
      manager.markLessonCompleted(lessonId);
      refreshData();
      
      // Persist progress to localStorage
      try {
        const completedIds = manager.modules
          .flatMap((m: any) => m.lessons)
          .filter((l: any) => l.completed)
          .map((l: any) => l.id);
        localStorage.setItem('app_curso_completed_lessons', JSON.stringify(completedIds));
      } catch (e) {
        console.error('Failed to save progress', e);
      }
    }
  };

  const handleAddLesson = async (moduleId: number, title: string, duration: string, content: string) => {
    if (manager) {
      manager.addLesson(moduleId, title, duration, content);
      refreshData();
      
      // Persist full content to localStorage
      try {
        const json = manager.exportToJson();
        localStorage.setItem('app_curso_content_json', json);

        // Try to persist to API if admin
        const token = localStorage.getItem('app_curso_token');
        if (token) {
          await fetch(getApiUrl(API_CONFIG.ENDPOINTS.CONTENT), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              moduleId,
              title,
              duration,
              markdown: content,
              images: []
            })
          });
        }
      } catch (e) {
        console.error('Failed to save content', e);
      }
    }
  };

  const goBack = () => {
    setView('home');
    setSelectedLesson(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-bold">Carregando conteúdo...</p>
        </div>
      </div>
    );
  }

  if (view === 'lesson' && selectedLesson) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] pb-20">
        <div className="max-w-4xl mx-auto px-4 pt-8">
          <button 
            onClick={goBack}
            className="flex items-center gap-2 text-indigo-600 font-bold mb-8 hover:bg-indigo-50 px-4 py-2 rounded-xl transition-all w-fit group"
          >
            <span className="group-hover:-translate-x-1 transition-transform">←</span> Voltar para o curso
          </button>

          <div className="bg-white rounded-[2.5rem] shadow-xl shadow-indigo-100/50 overflow-hidden mb-8 border border-indigo-50">
            <div className="aspect-video bg-gray-900 flex items-center justify-center text-white relative group">
              {selectedLesson.has_video ? (
                <div className="text-center">
                  <div className="w-20 h-20 bg-indigo-600 rounded-full flex items-center justify-center mb-4 mx-auto shadow-lg shadow-indigo-500/50 cursor-pointer hover:scale-110 transition-transform">
                    <span className="text-3xl ml-1">▶</span>
                  </div>
                  <p className="font-bold tracking-wide uppercase text-sm">Clique para iniciar vídeo</p>
                </div>
              ) : (
                <div className="text-center p-8">
                  <div className="w-20 h-20 bg-white/10 rounded-3xl flex items-center justify-center mb-4 mx-auto backdrop-blur-md">
                    <span className="text-4xl">📖</span>
                  </div>
                  <p className="text-gray-400 font-medium">Material de leitura e imagens</p>
                </div>
              )}
            </div>
            
            <div className="p-10">
              <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="bg-indigo-50 text-indigo-600 text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest border border-indigo-100">
                      Aula {selectedLesson.id}
                    </span>
                  </div>
                  <h1 className="text-4xl font-black text-gray-900 leading-tight">{selectedLesson.title}</h1>
                  <div className="flex items-center gap-4 mt-4 text-xs font-bold uppercase tracking-wider text-gray-400">
                    <span className="flex items-center gap-1.5">
                      <span className="text-indigo-500">⏱</span> {selectedLesson.duration}
                    </span>
                    <span className="flex items-center gap-1.5">
                      {selectedLesson.completed ? (
                        <><span className="text-green-500">✓</span> <span className="text-green-600">Concluída</span></>
                      ) : (
                        <><span className="text-orange-400">○</span> <span>Em progresso</span></>
                      )}
                    </span>
                  </div>
                </div>
              </div>

              <div className="prose prose-indigo max-w-none text-gray-600 leading-relaxed whitespace-pre-wrap font-medium text-lg">
                {selectedLesson.content}
              </div>

              <div className="mt-16 p-8 bg-indigo-50/50 rounded-[2rem] flex flex-col sm:flex-row items-center justify-between gap-6 border border-indigo-100">
                <div className="text-center sm:text-left">
                  <h3 className="font-black text-indigo-900 text-xl mb-1">Finalizou esta aula?</h3>
                  <p className="text-indigo-600/70 text-sm font-bold uppercase tracking-wide">Marque para salvar seu progresso</p>
                </div>
                {selectedLesson.completed ? (
                  <div className="bg-white text-green-600 px-8 py-4 rounded-2xl font-black border-2 border-green-100 flex items-center gap-3 shadow-sm">
                    <span className="text-xl">✓</span> CONCLUÍDA
                  </div>
                ) : (
                  <button 
                    onClick={() => handleMarkCompleted(selectedLesson.id)}
                    className="bg-indigo-600 text-white px-10 py-4 rounded-2xl font-black hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 active:scale-95"
                  >
                    MARCAR CONCLUÍDA
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleGreet = () => {
    if (window.wasm?.greet) {
      window.wasm.greet();
    } else {
      alert('WASM not loaded yet!');
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="max-w-xl mx-auto pb-12">
        <header className="px-6 pt-8 pb-4">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-black text-gray-900 tracking-tight">Curso US <span className="text-indigo-600">🦀</span></h1>
              <p className="text-gray-500 text-sm font-medium">Ultrassom em Reumatologia</p>
            </div>
            <div className="flex gap-2">
              <button 
                onClick={() => setShowAdmin(true)}
                className="w-12 h-12 rounded-2xl bg-white text-gray-400 hover:text-indigo-600 flex items-center justify-center transition-all shadow-sm hover:shadow-md active:scale-95"
                title="Admin Panel"
              >
                ⚙️
              </button>
              <button 
                onClick={handleGreet}
                className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all shadow-sm ${
                  wasmReady 
                    ? 'bg-white text-indigo-600 hover:shadow-md active:scale-95' 
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
                disabled={!wasmReady}
                title={wasmReady ? '🦀 WASM Greet' : 'Loading WASM...'}
              >
                {wasmReady ? '🦀' : '...'}
              </button>
            </div>
          </div>

          <ProgressHeader 
            progressPercentage={progress.progress_percentage}
            completedLessons={progress.completed_lessons}
            totalLessons={progress.total_lessons}
          />
        </header>

        <main className="px-6 py-4">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-black text-gray-900 uppercase tracking-tight">Módulos do Curso</h3>
            <span className="bg-indigo-50 text-indigo-700 text-[10px] font-bold px-2.5 py-1 rounded-full border border-indigo-100 uppercase tracking-wider">
              {modules.length} Módulos
            </span>
          </div>

          <div className="space-y-6">
            {modules.map((module) => (
              <ModuleCard 
                key={module.id} 
                module={module} 
                onLessonSelect={handleLessonSelect} 
              />
            ))}
          </div>
          
          <div className="mt-12 p-8 bg-white rounded-3xl border border-gray-100 shadow-sm text-center">
            <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center text-2xl mx-auto mb-4">
              ✨
            </div>
            <h4 className="font-bold text-gray-900 mb-2">App Modernizado</h4>
            <p className="text-sm text-gray-500 leading-relaxed">
              Vite + React + Rust WebAssembly para máxima performance e segurança.
            </p>
          </div>
        </main>
      </div>

      {showAdmin && (
        <AdminPanel 
          modules={modules} 
          onAddLesson={handleAddLesson} 
          onClose={() => setShowAdmin(false)} 
        />
      )}
    </div>
  );
}

export default App;
